import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { CampaignBreadcrumbs } from "@/components/CampaignBreadcrumbs";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, Pencil, X, ArrowLeft, MapPin, Trash2,
  Image as ImageIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Simple two-column labelled row helper for the view-mode grid.
const StatRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-0.5">{label}</p>
    <p className="text-sm text-foreground">{value || <span className="text-muted-foreground/50 italic">—</span>}</p>
  </div>
);

const NpcDetailPage = () => {
  const { campaignId, npcId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [npc, setNpc] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Editable draft fields (mirror the NPC model). Populated from the loaded
  // NPC when entering edit mode.
  const [draft, setDraft] = useState({
    name: "", race: "", profession: "", personality: "", description: "",
    location_id: "", armor_class: "", hit_points: "", speed: "", initiative: "",
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, n, locs] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/npcs/${npcId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ]);
      setCampaign(c.data);
      setNpc(n.data);
      setLocations(locs.data || []);
    } catch { toast.error("Failed to load NPC"); }
    finally { setLoading(false); }
  }, [campaignId, npcId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setImgError(false); }, [npc?.image_url]);

  const enterEdit = () => {
    if (!npc) return;
    setDraft({
      name: npc.name || "",
      race: npc.race || "",
      profession: npc.profession || "",
      personality: npc.personality || "",
      description: npc.description || "",
      location_id: npc.location_id || "",
      armor_class: npc.armor_class || "",
      hit_points: npc.hit_points || "",
      speed: npc.speed || "",
      initiative: npc.initiative || "",
    });
    setMode("edit");
  };

  const cancelEdit = () => setMode("view");

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/npcs/${npcId}`, { ...draft, campaign_id: campaignId, name: draft.name.trim() }, { withCredentials: true });
      setNpc(res.data);
      toast.success("NPC saved");
      setMode("view");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${npc.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/npcs/${npcId}`, { withCredentials: true });
      toast.success("NPC deleted");
      navigate(`/campaign/${campaignId}/npcs`);
    } catch { toast.error("Delete failed"); }
  };

  if (loading || !npc) {
    return (
      <Layout campaignName={campaign?.name}>
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      </Layout>
    );
  }

  const locationName = locations.find(l => l.id === npc.location_id)?.name;
  const portrait = npc.image_url && (npc.image_url.startsWith("http") ? npc.image_url : `${API}/files/${npc.image_url}`);
  const isEdit = mode === "edit";

  return (
    <Layout campaignName={campaign?.name} tabLabel={npc.name} hideBreadcrumbs>
      <CampaignBreadcrumbs
        campaignName={campaign?.name}
        extraCrumbs={[
          { label: npc.name },
        ]}
      />

      <div className="max-w-5xl mx-auto mt-4 space-y-6" data-testid="npc-detail-page">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/campaign/${campaignId}/npcs`)} className="mt-1">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to NPCs
          </Button>
          <div className="flex-1">
            {isEdit ? (
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="text-3xl font-serif h-12"
                data-testid="npc-name-input"
              />
            ) : (
              <h1 className="text-3xl font-serif text-foreground flex items-center gap-2">
                <Users className="w-6 h-6 text-primary" /> {npc.name}
              </h1>
            )}
            {locationName && !isEdit && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> {locationName}
              </p>
            )}
          </div>
          {!isEdit ? (
            <Button variant="outline" size="sm" onClick={enterEdit} data-testid="npc-enter-edit-btn">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="npc-cancel-btn">
                <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !draft.name.trim()} data-testid="npc-save-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
          {/* Portrait */}
          <div className="aspect-square bg-muted/20 border border-border rounded-sm overflow-hidden flex items-center justify-center" data-testid="npc-portrait">
            {portrait && !imgError ? (
              <img src={portrait} alt={npc.name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
            ) : (
              <ImageIcon className="w-10 h-10 text-muted-foreground/20" />
            )}
          </div>

          {/* Body */}
          {isEdit ? (
            <div className="space-y-4" data-testid="npc-edit-mode">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Race</label>
                  <Input value={draft.race} onChange={(e) => setDraft({ ...draft, race: e.target.value })} data-testid="npc-race-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Profession</label>
                  <Input value={draft.profession} onChange={(e) => setDraft({ ...draft, profession: e.target.value })} data-testid="npc-profession-input" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Location</label>
                <select
                  value={draft.location_id || ""}
                  onChange={(e) => setDraft({ ...draft, location_id: e.target.value })}
                  className="w-full h-9 rounded-sm border border-border bg-background px-3 text-sm"
                  data-testid="npc-location-select"
                >
                  <option value="">— None —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Personality</label>
                <Input value={draft.personality} onChange={(e) => setDraft({ ...draft, personality: e.target.value })} placeholder="Traits, mannerisms, flaws…" data-testid="npc-personality-input" />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">AC</label>
                  <Input value={draft.armor_class} onChange={(e) => setDraft({ ...draft, armor_class: e.target.value })} data-testid="npc-ac-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">HP</label>
                  <Input value={draft.hit_points} onChange={(e) => setDraft({ ...draft, hit_points: e.target.value })} data-testid="npc-hp-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Speed</label>
                  <Input value={draft.speed} onChange={(e) => setDraft({ ...draft, speed: e.target.value })} data-testid="npc-speed-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Init</label>
                  <Input value={draft.initiative} onChange={(e) => setDraft({ ...draft, initiative: e.target.value })} data-testid="npc-initiative-input" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1 block">Description</label>
                <div className="min-h-[280px] border border-border rounded-sm">
                  <RichTextEditor
                    value={draft.description}
                    onChange={(v) => setDraft({ ...draft, description: v })}
                    placeholder="Backstory, appearance, secrets, hooks…"
                    campaignId={campaignId}
                    fillHeight
                  />
                </div>
              </div>
              <div className="pt-2 border-t border-border">
                <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={remove} data-testid="npc-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete NPC
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="npc-view-mode">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatRow label="Race" value={npc.race} />
                <StatRow label="Profession" value={npc.profession} />
                <StatRow label="AC" value={npc.armor_class} />
                <StatRow label="HP" value={npc.hit_points} />
                <StatRow label="Speed" value={npc.speed} />
                <StatRow label="Initiative" value={npc.initiative} />
              </div>
              {npc.personality && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">Personality</p>
                  <p className="text-sm text-foreground/90">{npc.personality}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">Description</p>
                {npc.description ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none border border-border/60 rounded-sm px-4 py-3 bg-muted/10 min-h-[180px]">
                    <RichTextDisplay content={npc.description} />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground/60 italic border border-dashed border-border rounded-sm px-4 py-6 text-center">
                    No description yet — click Edit to add one.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NpcDetailPage;
